/**
 * Reusable hooks for Laabha.
 */

import { useCallback, useEffect, useState } from 'react';
import { loadSettings, saveSettings, type Settings } from './settings';

/** Reactive settings state — updates when settings change anywhere. */
export function useSettings(): [Settings, (s: Partial<Settings>) => void] {
  const [state, setState] = useState<Settings>(() => loadSettings());

  useEffect(() => {
    const handler = (e: Event) => setState((e as CustomEvent<Settings>).detail);
    window.addEventListener('axondesktop.settings.changed', handler as EventListener);
    return () =>
      window.removeEventListener('axondesktop.settings.changed', handler as EventListener);
  }, []);

  const update = useCallback((s: Partial<Settings>) => {
    setState(saveSettings(s));
  }, []);

  return [state, update];
}

/** Global keyboard shortcut binding. Pass chord like "cmd+k" or "ctrl+enter". */
export function useKeyboardShortcut(chord: string, handler: () => void) {
  useEffect(() => {
    const tokens = chord.toLowerCase().split('+');
    const targetKey = tokens.pop()!;
    const needCmd = tokens.includes('cmd') || tokens.includes('meta');
    const needCtrl = tokens.includes('ctrl');
    const needShift = tokens.includes('shift');
    const needAlt = tokens.includes('alt');

    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== targetKey) return;
      if (needCmd && !(e.metaKey || e.ctrlKey)) return;
      if (needCtrl && !e.ctrlKey) return;
      if (needShift !== e.shiftKey) return;
      if (needAlt !== e.altKey) return;
      e.preventDefault();
      handler();
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [chord, handler]);
}
